import React, { useState, useEffect } from "react";

import { Header, DataTable } from "../components";
import { Button, Checkbox, DatePicker, Divider, Table, Tag, Space, Typography } from "antd";
import { ThreeDot } from "react-loading-indicators";
import { fetchOrders, removeOrders } from "../utils/fetchOrders";
import { fetchDrivers } from "../utils/fetchDrivers";
import { fromDatetoYearMonthDay } from "../utils/utils";

import { timeout } from "../utils/utils";

import { List, message } from "antd";

import {
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  SyncOutlined,
  PlusCircleOutlined,
  FileZipOutlined,
  CopyOutlined,
  CheckCircleTwoTone,
} from "@ant-design/icons";

import { useParams, useNavigate, useLocation } from "react-router-dom";
import RouteStep1 from "../components/MakeRouteForm/RouteStep1";
import OrderSelectorDrawer from "../components/MakeRouteForm/OrderSelectorDrawer";

import {
  generateRoutePDF,
  generateExcelAllDrivers,
  generateLoadDriverPDF,
  generateExcelForBilling,
} from "../utils/generatePDFs";

import { fetchProducts, fetchAllProductsPrices } from "../utils/fetchProducts";
import {
  createReceiptForOrder,
  downloadReceiptPdf,
  downloadReceiptsFiltered,
} from "../api/receipts";
import { useAuth } from "../contexts/AuthProvider";
import { hasPermission } from "../utils/auth";

const DashboardDailyRoute = () => {
  const { user } = useAuth();
  const canWriteReceipts = hasPermission(user?.permissions, "receipts:write");

  const navigate = useNavigate();
  const location = useLocation();
  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  const params = useParams();
  const routeDay = String(params.routeDay);

  // If redirected from MakeRoute, orders are passed via router state → skip selection
  const preloadedOrders = location.state?.orders ?? null;

  const [viewMode, setViewMode] = useState(preloadedOrders ? "viewing" : "selecting");
  const [selectedOrderIds, setSelectedOrderIds] = useState(
    new Set(preloadedOrders?.map((o) => o.order_id) ?? [])
  );

  const [allOrders, setAllOrders] = useState([]);
  const [drivers, setDrivers] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);
  const [refreshPage, setRefreshPage] = useState(false);

  // Orders to use throughout the dashboard
  const displayOrders =
    viewMode === "viewing" && selectedOrderIds.size > 0
      ? allOrders.filter((o) => selectedOrderIds.has(o.order_id))
      : allOrders;

  const [selectorDrawerOpen, setSelectorDrawerOpen] = useState(false);

  const handleOrdersSelected = (orders) => {
    setSelectedOrderIds(new Set(orders.map((o) => o.order_id)));
    setViewMode("viewing");
  };

  const handleDrawerApply = (newSelectedIds) => {
    setSelectedOrderIds(newSelectedIds);
  };

  // Products Prices
  const [productsPrices, setProductsPrices] = useState([]);

  const [copied, setCopied] = useState(false);

  // Boletas
  const [localDocuments, setLocalDocuments] = useState({});
  const [creatingFor, setCreatingFor] = useState(new Set());
  const [downloadingFor, setDownloadingFor] = useState(new Set());
  const [creatingAll, setCreatingAll] = useState(false);
  const [createAllProgress, setCreateAllProgress] = useState(null);
  const [groupByDriver, setGroupByDriver] = useState(true);
  const [exportingAll, setExportingAll] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  useEffect(() => {
    if (!routeDay || routeDay === "undefined") return;

    setIsDataLoading(true);
    setDataError(null);

    const load = async () => {
      try {
        const [vals, driversData, products] = await Promise.all([
          fetchOrders(null, routeDay),
          fetchDrivers(),
          fetchProducts(),
        ]);
        setAllOrders(vals);
        setDrivers(driversData);
        const prices = await fetchAllProductsPrices(products);
        setProductsPrices(prices);
      } catch (error) {
        console.error(error);
        setDataError(error);
      } finally {
        setIsDataLoading(false);
      }
    };

    load();
  }, [refreshPage, routeDay]);

  const handleDownloadAllRoutes = () => {
    drivers.forEach((driver) => {
      if (
        displayOrders.filter(
          (item) =>
            item.delivery_info_array[item.delivery_info_array.length - 1]
              .driver_id === driver.personnel_id,
        ).length > 0
      ) {
        generateRoutePDF(
          displayOrders.filter(
            (item) =>
              item.delivery_info_array[item.delivery_info_array.length - 1]
                .driver_id === driver.personnel_id,
          ),
          driver.name,
          routeDay,
        );
      }
    });
  };

  const handleDownloadExcel = async () => {
    setExportingExcel(true);
    try {
      // Re-fetch orders so recently-created boletas (folios) are included,
      // since allOrders is only loaded on mount/refresh and boleta creation
      // doesn't update it.
      const freshOrders = await fetchOrders(null, routeDay);
      setAllOrders(freshOrders);
      const freshDisplayOrders =
        selectedOrderIds.size > 0
          ? freshOrders.filter((o) => selectedOrderIds.has(o.order_id))
          : freshOrders;
      generateExcelAllDrivers(freshDisplayOrders, drivers);
    } catch (err) {
      message.error(`Error al generar excel: ${err.message}`);
    } finally {
      setExportingExcel(false);
    }
  };

  const handleDownloadAllLoads = () => {
    drivers.forEach((driver) => {
      if (
        displayOrders.filter(
          (item) =>
            item.delivery_info_array[item.delivery_info_array.length - 1]
              .driver_id === driver.personnel_id,
        ).length > 0
      ) {
        generateLoadDriverPDF(
          displayOrders.filter(
            (item) =>
              item.delivery_info_array[item.delivery_info_array.length - 1]
                .driver_id === driver.personnel_id,
          ),
          driver.name,
          routeDay,
        );
      }
    });
  };

  useEffect(() => {
    const docs = {};
    allOrders.forEach((order) => {
      const boleta = order.documents?.find((d) => d.document_type === "BOLETA");
      if (boleta) docs[order.order_id] = boleta;
    });
    setLocalDocuments(docs);
  }, [allOrders]);

  const handleCreateSingle = async (orderId) => {
    setCreatingFor((prev) => new Set([...prev, orderId]));
    try {
      const doc = await createReceiptForOrder(orderId, routeDay);
      setLocalDocuments((prev) => ({ ...prev, [orderId]: doc }));
      message.success("Boleta creada");
    } catch (err) {
      message.error(`Error al crear boleta: ${err.message}`);
    } finally {
      setCreatingFor((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };

  const handleDownloadSingle = async (documentId, dailyId) => {
    setDownloadingFor((prev) => new Set([...prev, documentId]));
    try {
      await downloadReceiptPdf(documentId, dailyId);
    } catch (err) {
      message.error(`Error al descargar boleta: ${err.message}`);
    } finally {
      setDownloadingFor((prev) => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });
    }
  };

  const handleCreateAll = async () => {
    const pending = displayOrders.filter(
      (o) => !localDocuments[o.order_id] && o.order_type?.toUpperCase() !== "FACTURA"
    );
    if (pending.length === 0) {
      message.info("Todas las boletas ya están creadas");
      return;
    }
    setCreatingAll(true);
    setCreateAllProgress({ done: 0, total: pending.length });
    for (const order of pending) {
      setCreatingFor((prev) => new Set([...prev, order.order_id]));
      try {
        const doc = await createReceiptForOrder(order.order_id, routeDay);
        setLocalDocuments((prev) => ({ ...prev, [order.order_id]: doc }));
      } catch (err) {
        message.error(`Error al crear boleta para ${order.daily_id}: ${err.message}`);
      } finally {
        setCreatingFor((prev) => {
          const next = new Set(prev);
          next.delete(order.order_id);
          return next;
        });
        setCreateAllProgress((prev) =>
          prev ? { ...prev, done: prev.done + 1 } : null
        );
      }
    }
    setCreatingAll(false);
    setCreateAllProgress(null);
    message.success("Proceso completado");
  };

  const handleExportAll = async () => {
    setExportingAll(true);
    try {
      await downloadReceiptsFiltered(
        routeDay,
        displayOrders.map((o) => o.order_id),
        groupByDriver
      );
    } catch (err) {
      let errorMsg = `Error al exportar boletas: ${err.message}`;
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const data = JSON.parse(text);
          errorMsg = data.message || errorMsg;
        } catch {}
      }
      message.error(errorMsg);
    } finally {
      setExportingAll(false);
    }
  };

  const handleCreateEmailText = async () => {
    setCopied(true);
    const facturas = displayOrders.filter((order) => order.order_type === "Factura");
    const ordersNoStock = displayOrders.filter((order) => !order.rebaja_stock);
    const ordersNoBoleta = displayOrders.filter(
      (order) =>
        order.order_type?.toUpperCase() !== "FACTURA" &&
        !localDocuments[order.order_id],
    );
    const stringDate = routeDay.split("-");
    const date = new Date(stringDate[0], stringDate[1] - 1, stringDate[2]);
    const opciones = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    const fechaFormateada = new Intl.DateTimeFormat("es-CL", opciones).format(
      date,
    );

    const jackSellers = [
      "Jack D",
      "Jack M",
      "Web Distribuidorajack",
      "Elmundojack web",
    ];

    const pedidosJack = displayOrders.filter((order) =>
      jackSellers.includes(order.seller_name),
    );

    const email = `Buen día, \n\nAdjunto las cargas y rutas para ${fechaFormateada}. ${
      ordersNoStock.length > 0
        ? `\nLas boletas de los siguientes pedidos fueron creadas sin rebaja de stock: \n - ${ordersNoStock
            .map((order) => {
              const driverId = order.delivery_info_array.find(
                (delivery) => delivery.delivery_date === routeDay,
              ).driver_id;
              const driverName = drivers.find(
                (driver) => driver.personnel_id === driverId,
              ).name;
              return `${driverName}: ${order.daily_id}`;
            })
            .join(" \n - ")}\n`
        : "\nTodas las boletas se hicieron con rebaja de stock.\n"
    }${
      ordersNoBoleta.length > 0
        ? `\nFalta hacer la boleta de estos pedidos: \n - ${ordersNoBoleta
            .map((order) => {
              const driverId = order.delivery_info_array.find(
                (delivery) => delivery.delivery_date === routeDay,
              ).driver_id;
              const driverName = drivers.find(
                (driver) => driver.personnel_id === driverId,
              ).name;
              return `${driverName}: ${order.daily_id}`;
            })
            .join(" \n - ")}\n`
        : ""
    }${
      facturas.length > 0
        ? `\nLos siguientes pedidos requieren facturas: \n - ${facturas
            .map((order) => {
              const driverId = order.delivery_info_array.find(
                (delivery) => delivery.delivery_date === routeDay,
              ).driver_id;
              const driverName = drivers.find(
                (driver) => driver.personnel_id === driverId,
              ).name;
              return `${driverName}: ${order.daily_id}`;
            })
            .join(" \n - ")}\n`
        : "\nNo hay facturas.\n"
    }Saludos! \n\nFelipe Azúa Zagal
    `;
    navigator.clipboard.writeText(email);

    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  if (!routeDay || routeDay === "undefined") {
    return (
      <div className="flex flex-col items-center justify-center min-h-72 space-y-4">
        <div className="text-xl font-bold">Selecciona una fecha</div>
        <DatePicker
          size="large"
          onChange={(_, dateString) => {
            if (dateString) navigate(`/dashboardDailyRoute/${dateString}`);
          }}
        />
      </div>
    );
  }

  // Selection view (direct navigation or "Cambiar selección")
  if (viewMode === "selecting") {
    return (
      <div className="bg-white dark:bg-main-dark-bg rounded-3xl p-6">
        <div className="mb-6">
          <Header category="Ruta diaria" title={`Pedidos — ${routeDay}`} />
        </div>
        {isDataLoading ? (
          <div className="w-full text-center min-h-72 flex items-center justify-center">
            <ThreeDot variant="brick-stack" color="#4048F1" size="medium" text="" textColor="" />
          </div>
        ) : (
          <RouteStep1 routeDay={routeDay} onComplete={handleOrdersSelected} />
        )}
      </div>
    );
  }

  return (
    <>
      <div className="">
        {/* Showing data saved correctly */}
        <div className="flex w-full mb-3 gap-2">
          <Button
            icon={<SyncOutlined />}
            onClick={() => setRefreshPage((s) => !s)}
          />
          <Button onClick={() => setSelectorDrawerOpen(true)}>
            Pedidos ({displayOrders.length} / {allOrders.length})
          </Button>
          <Button onClick={() => setViewMode("selecting")}>
            Cambiar selección
          </Button>
        </div>

        <OrderSelectorDrawer
          open={selectorDrawerOpen}
          onClose={() => setSelectorDrawerOpen(false)}
          orders={allOrders}
          routeDay={routeDay}
          selectedIds={selectedOrderIds}
          onApply={handleDrawerApply}
          drivers={drivers ?? []}
        />
        <div className="">
          {isDataLoading ? (
            <div className="w-full text-center min-h-72 flex items-center justify-center">
              <ThreeDot
                variant="brick-stack"
                color="#4048F1"
                size="medium"
                text=""
                textColor=""
              />
            </div>
          ) : dataError ? (
            <div>Error al cargar pedidos.</div>
          ) : (
            <>
              <div className="w-full grid grid-cols-1 md:grid md:grid-cols-4 space-x-10">
                <div className="col-span-3">
                  <div className="flex items-end gap-x-4">
                    <div className="text-xl font-bold">Lista de Choferes</div>
                    {(() => {
                      const unassigned = displayOrders.filter((order) => {
                        const delivery = order.delivery_info_array.find(
                          (d) => d.delivery_date === routeDay,
                        );
                        return delivery && !delivery.driver_id;
                      }).length;
                      return unassigned > 0 ? (
                        <div className="text-xl italic text-red-700">
                          {unassigned} pedido{unassigned > 1 ? "s" : ""} sin asignar
                        </div>
                      ) : null;
                    })()}
                  </div>
                  <List>
                    {drivers
                      .filter(
                        (driver) =>
                          driver.name !== "No asignado" &&
                          displayOrders.filter(
                            (item) =>
                              item.delivery_info_array[
                                item.delivery_info_array.length - 1
                              ].driver_id === driver.personnel_id,
                          ).length > 0,
                      )
                      .map((driver) => {
                        const driverOrders = displayOrders.filter(
                          (item) =>
                            item.delivery_info_array[
                              item.delivery_info_array.length - 1
                            ].driver_id === driver.personnel_id,
                        );
                        return (
                          <List.Item className="flex justify-start">
                            <div className="w-10">{driver.name}</div>
                            <div>
                              <Button
                                onClick={() =>
                                  generateRoutePDF(driverOrders, driver.name, routeDay)
                                }
                              >
                                Descargar Ruta
                              </Button>
                              <Button
                                onClick={() =>
                                  generateLoadDriverPDF(driverOrders, driver.name, routeDay)
                                }
                              >
                                Descargar Carga
                              </Button>
                            </div>
                          </List.Item>
                        );
                      })}
                  </List>
                </div>
                <div className="flex flex-col space-y-3 col-span-1">
                  <div className="text-xl font-bold">Descarga Masiva</div>

                  <div className="flex w-full justify-between rounded-xl">
                    <div className="flex items-center space-x-2 ">
                      <FileExcelOutlined className="text-lg" />
                      <div>
                        Descargar <span className="font-bold">Excel</span>
                      </div>
                    </div>
                    <Button
                      onClick={handleDownloadExcel}
                      loading={exportingExcel}
                      disabled={exportingExcel}
                    >
                      <DownloadOutlined />
                    </Button>
                  </div>

                  <div className="flex w-full justify-between rounded-xl">
                    <div className="flex items-center space-x-2 ">
                      <FilePdfOutlined className="text-lg" />
                      <div>
                        Descargar todas las{" "}
                        <span className="font-bold">Rutas</span>
                      </div>
                    </div>
                    <Button onClick={handleDownloadAllRoutes}>
                      <DownloadOutlined />
                    </Button>
                  </div>

                  <div className="flex w-full justify-between rounded-xl">
                    <div className="flex items-center space-x-2 ">
                      <FilePdfOutlined className="text-lg" />
                      <div>
                        Descargar todas las{" "}
                        <span className="font-bold">Cargas</span>
                      </div>
                    </div>
                    <Button onClick={handleDownloadAllLoads}>
                      <DownloadOutlined />
                    </Button>
                  </div>
                  {/* <div className="flex w-full justify-between rounded-xl">
                    <div className="flex items-center space-x-2 ">
                      <FilePdfOutlined className="text-lg" />
                      <div>
                        Descargar Excel para{" "}
                        <span className="font-bold">Facturación</span>
                      </div>
                    </div>
                    <Button
                      onClick={() =>
                        generateExcelForBilling(
                          displayOrders,
                          productsPrices,
                          TIVENDO_VARIANT_MAPPING,
                          drivers,
                          routeDay
                        )
                      }
                    >
                      <DownloadOutlined />
                    </Button>
                  </div> */}
                  <div className="flex w-full justify-between rounded-xl">
                    <Button onClick={handleCreateEmailText}>
                      <CopyOutlined className="text-lg" />
                      <div className="flex items-center space-x-2 ">
                        {copied ? (
                          <div>
                            Correo copiado!{" "}
                            <CheckCircleTwoTone twoToneColor="#52c41a" />
                          </div>
                        ) : (
                          <div>
                            Generar Texto{" "}
                            <span className="font-bold">Correo</span>
                          </div>
                        )}
                      </div>
                    </Button>
                  </div>
                </div>
              </div>

              {/* ─── Boletas Section ─── */}
              <Divider />
              <div>
                <div className="flex justify-between items-center mb-4">
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    Creación de Boletas
                  </Typography.Title>
                  <Space size="large" align="center">
                    {createAllProgress && (
                      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                        {createAllProgress.done}/{createAllProgress.total}
                      </Typography.Text>
                    )}
                    {canWriteReceipts && (
                      <Button
                        icon={<PlusCircleOutlined />}
                        onClick={handleCreateAll}
                        loading={creatingAll}
                        disabled={creatingAll}
                      >
                        Crear todas
                      </Button>
                    )}
                    <div className="flex items-center gap-2">
                      <Button
                        icon={<FileZipOutlined />}
                        onClick={handleExportAll}
                        loading={exportingAll}
                        disabled={exportingAll || Object.keys(localDocuments).length === 0}
                      >
                        Exportar
                      </Button>
                      <Checkbox
                        checked={groupByDriver}
                        onChange={(e) => setGroupByDriver(e.target.checked)}
                      >
                        <Typography.Text style={{ fontSize: 12 }}>Agrupar por chofer</Typography.Text>
                      </Checkbox>
                    </div>
                  </Space>
                </div>

                <Table
                  dataSource={displayOrders}
                  rowKey="order_id"
                  size="small"
                  pagination={false}
                  columns={[
                    {
                      title: "ID",
                      dataIndex: "daily_id",
                      width: 90,
                      render: (id) => (
                        <Tag className="font-mono" style={{ fontSize: 11 }}>
                          {id}
                        </Tag>
                      ),
                    },
                    {
                      title: "Cliente",
                      dataIndex: "name",
                      render: (name, order) => (
                        <div>
                          <div className="font-medium text-sm">{name}</div>
                          {order.city && (
                            <div className="text-xs text-gray-400">
                              {order.city}
                            </div>
                          )}
                        </div>
                      ),
                    },
                    {
                      title: "Boleta",
                      width: 170,
                      render: (_, order) => {
                        const doc = localDocuments[order.order_id];
                        const isFactura = order.order_type?.toUpperCase() === "FACTURA";
                        if (doc) {
                          return (
                            <Tag color="green" style={{ fontSize: 11 }}>
                              <CheckCircleTwoTone
                                twoToneColor="#52c41a"
                                style={{ marginRight: 4 }}
                              />
                              {doc.document_id}
                            </Tag>
                          );
                        }
                        return isFactura ? (
                          <Tag color="blue" style={{ fontSize: 11 }}>
                            Factura
                          </Tag>
                        ) : (
                          <Tag color="orange" style={{ fontSize: 11 }}>
                            Sin boleta
                          </Tag>
                        );
                      },
                    },
                    {
                      title: "Acciones",
                      width: 160,
                      render: (_, order) => {
                        const doc = localDocuments[order.order_id];
                        const isCreating = creatingFor.has(order.order_id);
                        const isFactura = order.order_type?.toUpperCase() === "FACTURA";
                        const isDownloading = doc
                          ? downloadingFor.has(doc.id)
                          : false;
                        return (
                          <Space size="small">
                            <Button
                              size="small"
                              disabled={!!doc || isCreating || creatingAll || isFactura}
                              loading={isCreating}
                              title={isFactura ? "Este pedido requiere Factura" : undefined}
                              onClick={() => handleCreateSingle(order.order_id)}
                            >
                              Crear
                            </Button>
                            <Button
                              size="small"
                              icon={<DownloadOutlined />}
                              disabled={!doc || isDownloading}
                              loading={isDownloading}
                              onClick={() =>
                                handleDownloadSingle(doc.id, order.daily_id)
                              }
                            >
                              PDF
                            </Button>
                          </Space>
                        );
                      },
                    },
                  ]}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DashboardDailyRoute;

// Tivendo Variant Mapping
export const TIVENDO_VARIANT_MAPPING = {
  // Mapping based on Internal ID as key (variant ID)
  7: { code: "1", name: "MUNDOTITO", variant: "LAVANDA" },
  11: { code: "2", name: "EASY CLEAN", variant: "LAVANDA" },
  13: { code: "3", name: "EASY CLEAN", variant: "JAZMIN" },
  12: { code: "4", name: "EASY CLEAN", variant: "MANZANA" },
  204: { code: "5", name: "CHILEAN CAT LITTER", variant: "LAVANDA" },
  17: { code: "6", name: "CITY CAT", variant: "LAVANDA" },
  18: { code: "7", name: "CITY CAT", variant: "TALCO" },
  110: { code: "8", name: "CITY CAT", variant: "LIMON" },
  96: { code: "9", name: "EASY CLEAN", variant: "LAVANDA" },
  28: { code: "10", name: "LOVE SAND", variant: "LAVANDA" },
  193: { code: "11", name: "CATMANIA", variant: "LAVANDA" },
  30: { code: "12", name: "SAFARI", variant: "LAVANDA" },
  225: { code: "13", name: "NEO CLEAN", variant: "LAVANDA" },
  226: { code: "14", name: "NEO CLEAN", variant: "LIMON" },
  227: { code: "15", name: "NEO CLEAN", variant: "MANZANA" },
  121: { code: "16", name: "PET CLEAN", variant: "LAVANDA" },
  19: { code: "17", name: "MININO", variant: "LAVANDA" },
  21: { code: "18", name: "MININO", variant: "TALCO" },
  20: { code: "19", name: "MININO", variant: "MANZANA" },
  111: { code: "20", name: "EASY CLEAN", variant: "LAVANDA" },
  14: { code: "21", name: "EASY CLEAN", variant: "LAVANDA" },
  251: { code: "22", name: "SAFARI", variant: "LAVANDA" },
  31: { code: "23", name: "SAFARI", variant: "LAVANDA" },
  27: { code: "24", name: "BENGALA", variant: "LAVANDA" },
  218: { code: "25", name: "TURBO CLEAN", variant: "LAVANDA" },
  221: { code: "26", name: "TURBO CLEAN", variant: "LIMON" },
  220: { code: "27", name: "TURBO CLEAN", variant: "TALCO" },
  219: { code: "28", name: "TURBO CLEAN", variant: "MANZANA" },
  222: { code: "29", name: "TURBO CLEAN", variant: "CAFE" },
  6: { code: "30", name: "ARENA GRANEL", variant: "LAVANDA" },
  33: { code: "31", name: "SAFARI CRISTALES", variant: "NEUTRO" },
  194: { code: "32", name: "SAFARI CRISTALES", variant: "NEUTRO" },
  256: { code: "34", name: "ARENERO PETEGOU", variant: "CELESTE" },
  257: { code: "35", name: "ARENERO PETEGOU", variant: "GRIS" },
  258: { code: "37", name: "ARENERO PETEGOU", variant: "MORADO" },
  261: { code: "38", name: "ARENERO ROMEO", variant: "VERDE" },
  262: { code: "39", name: "ARENERO ROMEO", variant: "AMARILLO" },
  263: { code: "40", name: "ARENERO ROMEO", variant: "OCRE" },
  264: { code: "41", name: "ARENERO ROMEO", variant: "CELESTE" },
  265: { code: "42", name: "ARENERO ROMEO", variant: "MORADO" },
  266: { code: "45", name: "ARENERO CON VISERA", variant: "ROSA" },
  271: { code: "46", name: "ARENERO CON BANDEJA", variant: "CAFE" },
  85: {
    code: "47",
    name: "GALLETA CATS SNACK",
    variant: "SALMON//POLLO RELLENA",
  },
  82: { code: "48", name: "GALLETA CATS SNACK", variant: "ATUN/QUESO RELLENA" },
  84: {
    code: "49",
    name: "GALLETA CATS SNACK",
    variant: "SALMON/HIERBA RELLENA",
  },
  83: { code: "50", name: "GALLETA CATS SNACK", variant: "ATUN/POLLO RELLENA" },
  282: { code: "138", name: "GALLETA RELLENA", variant: "ATUN/OSTION" },
  287: { code: "143", name: "GALLETA RELLENA", variant: "ATUN/OSTION" },
  223: { code: "51", name: "GALLETA CATS SNACK", variant: "SALMON/HIERBA" },
  86: { code: "52", name: "GALLETA CATS SNACK", variant: "ATUN/HIERBA" },
  87: { code: "53", name: "GALLETA CATS SNACK", variant: "POLLO/HIERBA" },
  88: {
    code: "54",
    name: "GALLETA CATS SNACK",
    variant: "SALMON/POLLO/HIERBA",
  },
  312: { code: "72", name: "WHISKAS SACHET SARDINA", variant: "SARDINA" }, // Caja de 12 unidades ARREGLAR
  314: { code: "70", name: "WHISKAS SACHET POLLO", variant: "POLLO" }, // Caja de 12 unidades ARREGLAR
  280: { code: "55", name: "SNACK CREMOSO CATS SNACK", variant: "SALMON" }, // should be 'SALMON/HIERBA'
  186: { code: "55", name: "SNACK CREMOSO CATS SNACK", variant: "SALMON" },
  191: { code: "56", name: "SNACK CREMOSO CATS SNACK", variant: "MEJILLON" },
  189: {
    code: "57",
    name: "SNACK CREMOSO CATS SNACK",
    variant: "HIGADO DE POLLO",
  },
  190: { code: "58", name: "SNACK CREMOSO CATS SNACK", variant: "CANGREJO" },
  187: { code: "59", name: "SNACK CREMOSO CATS SNACK", variant: "ALBACORA" },
  76: { code: "63", name: "CHURU", variant: "POLLO/SALMON" },
  77: { code: "64", name: "CHURU", variant: "ATUN" },
  78: { code: "65", name: "CHURU", variant: "ATUN/SALMON" },
  79: { code: "66", name: "CHURU", variant: "POLLO/CARNE/QUESO" },
  80: { code: "67", name: "CHURU", variant: "POLLO" },
  81: { code: "68", name: "CHURU", variant: "ATUN/OSTION" },
  159: { code: "69", name: "WHISKAS SACHET", variant: "CARNE" },
  157: { code: "70", name: "WHISKAS SACHET", variant: "POLLO" },
  232: { code: "71", name: "WHISKAS SACHET", variant: "SALMON" },
  158: { code: "72", name: "WHISKAS SACHET", variant: "SARDINA" },
  160: { code: "74", name: "WHISKAS SACHET", variant: "GATITOS" },
  164: { code: "75", name: "FELIX SACHET", variant: "CARNE" },
  162: { code: "76", name: "FELIX SACHET", variant: "POLLO E HIGADO" },
  161: { code: "77", name: "FELIX SACHET", variant: "SALMON" },
  213: { code: "78", name: "FELIX SACHET", variant: "ATUN" },
  195: { code: "79", name: "FELIX SACHET", variant: "PAVO" },
  196: { code: "81", name: "FELIX SACHET", variant: "GATITOS CARNE" },
  129: { code: "82", name: "MASTER CAT SACHET", variant: "SENIOR POLLO" },
  38: { code: "84", name: "FIT FORMULA", variant: "PESCADO" },
  102: { code: "85", name: "NOMADE", variant: "PESCADO" },
  175: { code: "86", name: "WHISKAS", variant: "POLLO" },
  173: { code: "87", name: "WHISKAS", variant: "CARNE" },
  176: { code: "88", name: "WHISKAS", variant: "PESCADO" },
  233: { code: "89", name: "WHISKAS", variant: "GATITO" },
  168: { code: "90", name: "CAT CHOW", variant: "DELIMIX" },
  172: { code: "91", name: "CAT CHOW", variant: "ESTERILIZADO" },
  171: { code: "92", name: "CAT CHOW", variant: "GATITO" },
  169: { code: "93", name: "CAT CHOW", variant: "PESCADO" },
  170: { code: "94", name: "CAT CHOW", variant: "CARNE" },
  330: { code: "95", name: "CAT CHOW", variant: "DELIMIX" },
  331: { code: "96", name: "CAT CHOW", variant: "ESTERILIZADO" },
  334: { code: "97", name: "CAT CHOW", variant: "GATITO" },
  333: { code: "98", name: "CAT CHOW", variant: "PESCADO" },
  332: { code: "99", name: "CAT CHOW", variant: "CARNE" },
  327: { code: "100", name: "RASCADOR", variant: "UNICO" },
  177: { code: "101", name: "BEBEDERO CHICO", variant: "UNICO" },
  275: { code: "102", name: "BEBEDERO GRANDE", variant: "UNICO" },
  338: { code: "104", name: "BOLSA DE BASURA", variant: "MIX" },
  231: { code: "126", name: "WHISKAS SACHET", variant: "PAVO" },
  273: { code: "129", name: "FELIX MEGAMIX", variant: "ADULTO" },
  276: { code: "130", name: "SNACK CREMOSO", variant: "CAMARON" },
  300: { code: "133", name: "SNACK CREMOSO", variant: "SALMON/MATATABI" },
  289: { code: "134", name: "SNACK CREMOSO", variant: "CONEJO" },
  336: { code: "145", name: "KTITO", variant: "ADULTO" },
  291: { code: "146", name: "EASY CLEAN", variant: "MOKA" },
  278: { code: "148", name: "GALLETA CATS SNACK", variant: "CANGREJO/HIERBA" },
  288: { code: "149", name: "GALLETA CATS SNACK", variant: "CAMARON/HIERBA" },
  293: { code: "152", name: "ALFOMBRA ATRAPA ARENA", variant: "ROSADA" },
  301: { code: "153", name: "ALFOMBRA ATRAPA ARENA", variant: "NEGRA" },
  302: { code: "155", name: "ALFOMBRA ATRAPA ARENA", variant: "BEIGE" },
  339: { code: "156", name: "SPECIAL CAT ADULTO MIX", variant: "UNICO" },
  295: { code: "157", name: "Misifus Arena", variant: "LAVANDA" },
  297: { code: "158", name: "Misifus Arena", variant: "TALCO" },
  296: { code: "159", name: "Misifus Arena", variant: "MANZANA" },
  298: { code: "160", name: "Misifus Arena", variant: "LIMON" },
  299: { code: "161", name: "Misifus Arena", variant: "MOKA" },
  335: {
    code: "163",
    name: "Arena Silica Cristales cat litter",
    variant: "NEUTRO",
  },
  281: {
    code: "DESPACHO",
    name: "DESPACHO",
    variant: "",
  },
  342: { code: "109", name: "RASCADOR CON PLAY SET", variant: "UNICO" },

  284: { code: "138", name: "DELICIUS CATNIP", variant: "OMEGA/SALMON" },
  285: { code: "137", name: "DELICIUS CATNIP", variant: "OMEGA/POLLO" },
  286: { code: "136", name: "DELICIUS CATNIP", variant: "OMEGA/ATUN" },
};

const LoadingComponent = ({ receiptRequest }) => {
  console.log("this is the receiptRequest", receiptRequest);
  return (
    <div>
      {receiptRequest?.map((item) => (
        <div>{item.status}</div>
      ))}
    </div>
  );
};
